import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { message } from 'antd';
import * as md5 from 'md5';
import { getServerHost } from '../config';
import store from '../store';

export interface RequestOptions extends AxiosRequestConfig {
  label?: string;
}

interface InterfaceOptions {
  baseURL: string;
  timeout: number;
}

export interface CRes<T = any> {
  code: number;
  message?: string;
  data: T;
}

const getSecret = (orgCode: string, pid: string, timestamp: number, salt: string) =>
  md5(orgCode + pid + timestamp + salt);

const getHeaders = () => {
  const headers: Record<string, any> = {};
  headers.authorization = store.authorization;

  const ts = Date.now();

  headers['yk-data-o'] = store.orgCode;
  headers['yk-data-p'] = store.pid;
  headers['yk-data-t'] = ts;
  headers['yk-data-v'] = getSecret(store.orgCode, store.pid, ts, store.salt);

  return headers;
};

const getBaseURL = (options: RequestOptions = {}) => {
  if (options.baseURL !== undefined) {
    return options.baseURL;
  }
  return store.main_base_url ? store.main_base_url : getServerHost(store.orgCode);
};

export class CRequest {
  // 创建实例
  create(options: InterfaceOptions) {
    const conf = {
      baseURL: options.baseURL,
      timeout: options.timeout,
      headers: {
        ...getHeaders(),
        'Content-Type': 'application/json; charset=utf-8',
      },
    };
    return axios.create(conf);
  }

  // 请求拦截
  interceptors(instance: AxiosInstance, options: RequestOptions, try_count = 3) {
    // 添加请求拦截器
    instance.interceptors.request.use(
      (config) => config,
      (error) => {
        console.log(error);
        return Promise.reject(error);
      },
    );

    // 添加响应拦截器
    instance.interceptors.response.use(
      (res) => {
        const { data } = res;
        return data;
      },
      (error) => {
        if (error) {
          if (error.response) {
            if (error.response.status) {
              if (try_count > 0 && error.response.status === 502) {
                return setTimeout(() => {
                  instance(options);
                  try_count--;
                }, 1000);
              }
              message.error(`服务器错误:${error.message}`, 3);
            }
          } else {
            if (error.message === 'Network Error') {
              message.error('网络错误,可能是无法连接到服务器', 3);
            } else {
              message.error('服务内部错误', 3);
            }
          }
          return Promise.reject(error);
        }
      },
    );
  }

  request<T = any>(options: RequestOptions = {}): Promise<CRes<T>> {
    const baseURL = getBaseURL(options);
    const timeout = options.timeout || 60000;
    const instance = this.create({ baseURL, timeout });
    this.interceptors(instance, options);
    return instance(options) as any;
  }
}

const requestInstance = new CRequest();

async function request<R = any>(options: RequestOptions) {
  const res: any = await requestInstance.request(options);
  return res as CRes<R>;
}

export default request;
